import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import StyledTable from "@/components/styled/StyledTable";
import { Custom } from "@/types/api/api-types";
import { useGetCustoms } from "@/api/customs.api";
import { useEffect, useState } from "react";
import Paths from "@/constants/paths";
import { generatePath, useNavigate } from "react-router-dom";
import { FileText, Archive, ChevronRight } from "lucide-react";
import { CustomArchiveModal } from "../customs/customArchiveModal";
import { Button } from "@/components/ui/button";
import { useAtomValue } from "jotai";
import { userAtom } from "@/stores/authStore";
import { useRegionCustomOptions } from "@/hooks/useEnumOptions";

const columnHelper = createColumnHelper<Custom>();

type CustomTableProps = {
  selectedRegion: number;
  searchField: string;
  customQuotationOnly?: string;
};

export const CustomTable = ({
  selectedRegion,
  searchField,
  customQuotationOnly,
}: CustomTableProps) => {
  const { t } = useTranslation(["boParameters", "common"]);
  const regionCustomOptions = useRegionCustomOptions();
  const [filteredCustom, setFilteredCustom] = useState<Custom[] | undefined>([]);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [archiveModalCustom, setArchiveModalCustoms] = useState<Custom | null>(null);

  const { data: customs } = useGetCustoms();

  const handleArchiveModalOpen = (custom: Custom) => {
    setArchiveModalCustoms(custom);
    setIsArchiveModalOpen(true);
  };

  const handleArchiveModalClose = () => {
    setArchiveModalCustoms(null);
    setIsArchiveModalOpen(false);
  };

  const navigate = useNavigate();
  const user = useAtomValue(userAtom);
  const isSuperAdmin = user?.role === "SUPERADMIN";

  useEffect(() => {
    const tmpCustoms = customs?.filter((custom) => {
      if (customQuotationOnly && String(!!custom.isCustomQuotationOnly) !== customQuotationOnly)
        return false;
      if (selectedRegion === 0) return true;
      if (selectedRegion > regionCustomOptions.length) return false;
      return custom.region === regionCustomOptions[selectedRegion - 1].value;
    });
    if (searchField.length === 0)
      setFilteredCustom(tmpCustoms?.sort((a, b) => a.label.localeCompare(b.label)));
    else
      setFilteredCustom(
        tmpCustoms
          ?.filter(
            (custom) => custom.label.toLowerCase().indexOf(searchField.toLocaleLowerCase()) !== -1,
          )
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
  }, [searchField, customs, selectedRegion, customQuotationOnly, regionCustomOptions]);

  const columns = [
    columnHelper.display({
      id: "label",
      header: t("customTable.labelHeader"),
      cell: (info) => (
        <button
          className="flex h-full w-full items-center gap-2 focus:outline-hidden"
          disabled={!isSuperAdmin}
          onClick={() =>
            navigate(`${Paths.bo.customs}?id=${info.row.original.id}`, {
              state: { fromParameters: true },
            })
          }
        >
          {typeof info.row.original.flagCode === "string" && (
            <img
              src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${info.row.original.flagCode}.svg`}
              className="h-4 w-4"
            />
          )}
          <span>
            {info.row.original.isArchived
              ? t("customTable.archivedLabel", { label: info.row.original.label })
              : info.row.original.label}
          </span>
        </button>
      ),
    }),

    columnHelper.display({
      id: "nbBtls",
      header: t("customTable.bottleMaxHeader"),
      cell: (info) => (
        <span>
          {info.row.original.bottleMaxPerShipment === null
            ? t("customTable.noLimit")
            : typeof info.row.original.bottleMaxPerShipment === "string"
              ? info.row.original.bottleMaxPerShipment
              : typeof info.row.original.bottleMaxPerShipment === "number"
                ? info.row.original.bottleMaxPerShipment
                : t("customTable.invalidValue")}
        </span>
      ),
    }),
    columnHelper.display({
      id: "isCustomQuotationOnly",
      header: t("customTable.customQuotationOnlyHeader"),
      cell: (info) => (
        <span>
          {info.row.original.isCustomQuotationOnly
            ? t("common:boolean.yes")
            : t("common:boolean.no")}
        </span>
      ),
    }),
    columnHelper.display({
      id: "action",
      header: "",
      cell: (info) => (
        <div className="flex flex-row gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate(generatePath(Paths.bo.customPolicy, { customsId: info.row.original.id }))
            }
            title={t("customTable.customPolicy")}
          >
            <FileText className="size-8 text-gray-600" style={{ height: "20px", width: "auto" }} />
          </Button>
          {isSuperAdmin && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleArchiveModalOpen(info.row.original)}
                title={t("customTable.archive")}
                className="mr-10"
              >
                <Archive
                  className="size-8 text-gray-600"
                  style={{ height: "20px", width: "auto" }}
                />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                title={t("customTable.details")}
                onClick={() =>
                  navigate(`${Paths.bo.customs}?id=${info.row.original.id}`, {
                    state: { fromParameters: true },
                  })
                }
              >
                <ChevronRight
                  className="size-8 text-gray-600"
                  style={{ height: "24px", width: "auto" }}
                />
              </Button>
            </>
          )}
        </div>
      ),
    }),
  ];

  return (
    <>
      <StyledTable data={filteredCustom || []} columns={columns} />
      {archiveModalCustom && (
        <CustomArchiveModal
          open={isArchiveModalOpen}
          onClose={handleArchiveModalClose}
          custom={archiveModalCustom}
        />
      )}
    </>
  );
};
